import {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {
    focusRequiredTabField,
    getRequiredTabKeys,
    REQUIRED_FIELDS_STATE_EVENT,
} from "../utils/project-utils.js";

const getRequiredScope = (scopeRef) => scopeRef?.current ?? document;

const deferFocus = (tabKey, scopeRef) => {
    const focus = () => focusRequiredTabField(tabKey, getRequiredScope(scopeRef));
    const requestFrame = globalThis.requestAnimationFrame;

    if (!requestFrame) {
        globalThis.setTimeout(focus, 0);
        return;
    }

    requestFrame(() => requestFrame(focus));
};

const areEqual = (currentKeys, nextKeys) => currentKeys.length === nextKeys.length
    && currentKeys.every((key, index) => key === nextKeys[index]);

export const useRequiredTabErrors = ({tabKeys = [], tabSelected, setTabSelected, scopeRef} = {}) => {
    const [invalidTabs, setInvalidTabs] = useState(() => getRequiredTabKeys(getRequiredScope(scopeRef)));
    const tabKeysRef = useRef(tabKeys);
    const tabSelectedRef = useRef(tabSelected);
    const setTabSelectedRef = useRef(setTabSelected);
    const pendingTabRef = useRef(null);

    const syncInvalidTabs = useCallback((keys) => {
        const nextKeys = [...new Set(keys ?? [])];
        setInvalidTabs((currentKeys) => areEqual(currentKeys, nextKeys) ? currentKeys : nextKeys);
    }, []);

    useEffect(() => {
        tabKeysRef.current = tabKeys;
        tabSelectedRef.current = tabSelected;
        setTabSelectedRef.current = setTabSelected;
        syncInvalidTabs(getRequiredTabKeys(getRequiredScope(scopeRef)));
    }, [tabKeys, tabSelected, setTabSelected, scopeRef, syncInvalidTabs]);

    useEffect(() => {
        const handleRequiredFieldsState = (event) => {
            syncInvalidTabs(getRequiredTabKeys(getRequiredScope(scopeRef)));

            const firstInvalidTab = event.detail?.firstInvalidTab;
            if (event.detail?.focusFirstInvalid === false) {
                pendingTabRef.current = null;
                return;
            }

            if (!firstInvalidTab || !tabKeysRef.current.includes(firstInvalidTab)) {
                pendingTabRef.current = null;
                return;
            }

            if (firstInvalidTab === tabSelectedRef.current) {
                deferFocus(firstInvalidTab, scopeRef);
                return;
            }

            pendingTabRef.current = firstInvalidTab;
            setTabSelectedRef.current?.(firstInvalidTab);
        };

        window.addEventListener(REQUIRED_FIELDS_STATE_EVENT, handleRequiredFieldsState);
        syncInvalidTabs(getRequiredTabKeys(getRequiredScope(scopeRef)));

        return () => window.removeEventListener(REQUIRED_FIELDS_STATE_EVENT, handleRequiredFieldsState);
    }, [scopeRef, syncInvalidTabs]);

    useEffect(() => {
        if (pendingTabRef.current !== tabSelected) return;

        const tabKey = pendingTabRef.current;
        pendingTabRef.current = null;
        deferFocus(tabKey, scopeRef);
    }, [scopeRef, tabSelected]);

    const hasRequiredTabError = useCallback((tabKey) => invalidTabs.includes(tabKey), [invalidTabs]);

    return {invalidTabs, hasRequiredTabError};
};

export const useInactiveTabAutofillProtection = (containerRef, activeTab) => {
    useLayoutEffect(() => {
        const container = containerRef?.current;
        if (!container) return undefined;

        const updateAutocomplete = () => {
            container.querySelectorAll("[data-required-tab]").forEach((tab) => {
                const isInactive = tab.dataset.requiredTab !== activeTab;

                tab.querySelectorAll("input, textarea, select").forEach((field) => {
                    if (isInactive) {
                        if (field.dataset.requiredPreviousAutocomplete === undefined) {
                            const autocomplete = field.getAttribute("autocomplete");
                            field.dataset.requiredPreviousAutocomplete = autocomplete ?? "";
                            field.dataset.requiredHadAutocomplete = autocomplete === null ? "false" : "true";
                        }

                        if (field.getAttribute("autocomplete") !== "off") field.setAttribute("autocomplete", "off");
                        return;
                    }

                    if (field.dataset.requiredPreviousAutocomplete === undefined) return;

                    if (field.dataset.requiredHadAutocomplete === "true") {
                        field.setAttribute("autocomplete", field.dataset.requiredPreviousAutocomplete);
                    } else {
                        field.removeAttribute("autocomplete");
                    }

                    delete field.dataset.requiredPreviousAutocomplete;
                    delete field.dataset.requiredHadAutocomplete;
                });
            });
        };

        updateAutocomplete();

        const observer = typeof MutationObserver === "undefined"
            ? null
            : new MutationObserver(updateAutocomplete);

        observer?.observe(container, {childList: true, subtree: true, attributes: true, attributeFilter: ["autocomplete"]});

        return () => observer?.disconnect();
    }, [activeTab, containerRef]);
};